import React, { useMemo } from 'react';
import { useLanguageStore } from '../../../stores/languageStore';
import type { SurveyProgressProps } from '../SurveyPage.types';

/**
 * SurveyProgress Component
 *
 * Displays a progress bar and text indicating the user's progress through the survey.
 * Shows both a visual progress bar and a textual representation (e.g., "Page 2 of 5").
 *
 * The values passed in represent logical progress steps from the user's perspective:
 * - When questions are paginated, each "Next" press is a step.
 * - When a page shows all questions at once, that entire page counts as a single step.
 *
 * @param props - Component props
 * @returns The rendered progress indicator
 *
 * @example
 * <SurveyProgress
 *   currentIndex={1}
 *   totalQuestions={3}
 * />
 */
export const SurveyProgress: React.FC<SurveyProgressProps> = ({
  currentIndex,
  totalQuestions,
}) => {
  const languageStore = useLanguageStore();

  // Calculate progress percentage (1-based for display)
  const progress = useMemo(() => {
    return ((currentIndex + 1) / totalQuestions) * 100;
  }, [currentIndex, totalQuestions]);

  // Format the progress text with proper localization
  const progressText = useMemo(() => {
    return languageStore.formatPageOf(currentIndex + 1, totalQuestions);
  }, [languageStore, currentIndex, totalQuestions]);

  return (
    <div className="progress-indicator">
      <div className="progress-bar">
        <div
          className="progress-bar-fill"
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="progress-text">{progressText}</p>
    </div>
  );
};
